import { create } from 'zustand/react';
import { immer } from 'zustand/middleware/immer';
// Import immer so TypeScript can reference them in .d.ts files
// This fixes the "cannot be named without a reference" error with pnpm isolation
// find more info here https://github.com/microsoft/TypeScript/pull/58176#issuecomment-2052698294
import 'immer';
import { devtools } from 'zustand/middleware';
import { createContext, type PropsWithChildren, useRef } from 'react';
import { mapSearchStateToSearchListingsFilters } from '@pkgs/components/utils/mapFilterFields';
import { type SearchListingsFilters } from '@ev-tech/search-modules-api';

import { createUtilSlice, extractStateFromStore, type UtilStore } from './slices/utilSlice';
import { createShopSlice, defaultShopState, type ShopState, type ShopStore } from './slices/shopSlice';
import {
  createSearchModeSlice,
  defaultSearchModeState,
  type SearchMode,
  type SearchModeState,
  type SearchModeStore,
} from './slices/searchModeSlice';
import { createRangesSlice, defaultRangesState, type RangesState, type RangesStore } from './slices/rangesSlice';
import {
  createPrivateOfficeSlice,
  defaultPrivateOfficeState,
  type PrivateOfficeState,
  type PrivateOfficeStore,
} from './slices/privateOfficeSlice';
import { createPreferencesSlice, defaultPreferencesState, type PreferencesState, type PreferencesStore } from './slices/preferencesSlice';
import { createOptionsSlice, defaultOptionsState, type OptionsState, type OptionsStore } from './slices/optionsSlice';
import {
  createObjectTypeFilterSlice,
  defaultObjectTypeFilterState,
  type ObjectTypeFilterState,
  type ObjectTypeFilterStore,
} from './slices/objectTypeFilterSlice';
import {
  createMapInteractionSlice,
  defaultMapInteractionState,
  type MapInteractionState,
  type MapInteractionStore,
} from './slices/mapInteractionSlice';
import { createLocationSlice, defaultLocationState, type LocationState, type LocationStore } from './slices/locationSlice';
import {
  createLifestyleCategorySlice,
  defaultLifestyleCategoryState,
  type LifestyleCategoryState,
  type LifestyleCategoryStore,
} from './slices/lifestyleCategorySlice';
import { createFeaturesSlice, defaultFeaturesState, type FeaturesState, type FeaturesStore } from './slices/featuresSlice';
import { beforeStateChange } from './beforeStateChange';
import { getDefaultSortingOptionId } from '../../../utils/searchStateUtils';
import { isProductionClient } from '../../../consts/environment';

declare global {
  interface Window {
    getStateSnapshot: () => SearchState;
  }
}

export type MiddlewareType = [['zustand/devtools', never], ['zustand/immer', never]];

// type of state of all slices combined
export type SearchState = ObjectTypeFilterState &
  FeaturesState &
  RangesState &
  ShopState &
  OptionsState &
  PrivateOfficeState &
  LifestyleCategoryState &
  LocationState &
  MapInteractionState &
  SearchModeState &
  PreferencesState;

// type of state and actions of all slices combined
export type SearchStore = ObjectTypeFilterStore &
  FeaturesStore &
  RangesStore &
  UtilStore &
  ShopStore &
  OptionsStore &
  PrivateOfficeStore &
  LifestyleCategoryStore &
  LocationStore &
  MapInteractionStore &
  SearchModeStore &
  PreferencesStore;

export type BeforeFiltersChange = (filters: SearchListingsFilters, searchMode: SearchMode) => void | { redirectDidHappen?: boolean };

export const createSearchStore = (
  initState: Partial<SearchState> = {},
  namespace = 'search-store',
  onBeforeFiltersChange?: BeforeFiltersChange,
) => {
  const initialState = {
    ...initState,
  };

  // if sortingOptions is not provided, set it to the default sorting options based on the marketing type
  if (!initialState.sortingOptionId) {
    initialState.sortingOptionId = getDefaultSortingOptionId(initState.marketingType);
  }

  const onBeforeStateChange = (proposedState: SearchStore) =>
    onBeforeFiltersChange?.(mapSearchStateToSearchListingsFilters(proposedState), proposedState.searchMode);

  return create<SearchStore>()(
    devtools(
      beforeStateChange(
        immer((...args) => ({
          ...createObjectTypeFilterSlice(...args),
          ...createFeaturesSlice(...args),
          ...createRangesSlice(...args),
          ...createShopSlice(...args),
          ...createLifestyleCategorySlice(...args),
          ...createOptionsSlice(...args),
          ...createPrivateOfficeSlice(...args),
          ...createLocationSlice(...args),
          ...createMapInteractionSlice(...args),
          ...createUtilSlice(...args),
          ...createSearchModeSlice(...args),
          ...createPreferencesSlice(...args),
          ...initialState,
        })),
        onBeforeStateChange,
      ),
      {
        name: 'search-modules',
        store: namespace,
        enabled: !isProductionClient,
      },
    ),
  );
};
export type SearchStoreApi = ReturnType<typeof createSearchStore>;

export const SearchStoreContext = createContext<SearchStoreApi | null>(null);

export type SearchStoreProviderProps = PropsWithChildren<{
  initialState?: Partial<SearchState> | (() => Partial<SearchState>);
  namespace?: string;
  onBeforeFiltersChange?: BeforeFiltersChange;
}>;

export const SearchStoreProvider = ({ children, initialState, namespace, onBeforeFiltersChange }: SearchStoreProviderProps) => {
  const storeRef = useRef<SearchStoreApi | null>(null);

  if (storeRef.current === null) {
    storeRef.current = createSearchStore(
      typeof initialState === 'function' ? initialState() : initialState,
      namespace,
      onBeforeFiltersChange,
    );
    if (typeof window !== 'undefined') {
      // make the state snapshot available in the window object, so it can be read by extensions
      window.getStateSnapshot = () => extractStateFromStore(storeRef.current?.getState() ?? {});
    }
  }

  return <SearchStoreContext.Provider value={storeRef.current}>{children}</SearchStoreContext.Provider>;
};

export const defaultSearchState: SearchState = {
  ...defaultObjectTypeFilterState,
  ...defaultFeaturesState,
  ...defaultRangesState,
  ...defaultOptionsState,
  ...defaultShopState,
  ...defaultPrivateOfficeState,
  ...defaultLifestyleCategoryState,
  ...defaultLocationState,
  ...defaultMapInteractionState,
  ...defaultSearchModeState,
  ...defaultPreferencesState,
};
